import {useCallback, useState} from 'react'
import {useQuery, useQueryClient} from 'react-query'
import {
  DASHBOARD_ACTIVITY_REFETCH_MS,
  DASHBOARD_LIVE_ACTIVE_REFETCH_MS,
  DASHBOARD_LIVE_IDLE_REFETCH_MS,
  getDashboardActivity,
  getDashboardActivityQueryKey,
  getDashboardLive,
  getDashboardLiveQueryKey,
  shouldRetryDashboardRead,
} from './_requests'
import {DashboardActivitySnapshot, DashboardLiveSnapshot} from './_models'

// Something is moving: poll often. Otherwise a slow heartbeat is enough.
export const isLiveSnapshotBusy = (snapshot?: DashboardLiveSnapshot) =>
  Boolean(
    snapshot &&
      (snapshot.operations?.some((operation) => operation.state !== 'stalled') ||
        snapshot.streams?.length ||
        Number(snapshot.sending?.pending || 0) > 0)
  )

// One read shared by the activity feed, the error digest and the goods/services list.
export const useDashboardActivity = (companyId: number) => {
  const queryClient = useQueryClient()
  const [refreshing, setRefreshing] = useState(false)
  const query = useQuery<DashboardActivitySnapshot>(
    getDashboardActivityQueryKey(companyId),
    ({signal}) => getDashboardActivity(false, signal),
    {
      enabled: companyId > 0,
      keepPreviousData: true,
      staleTime: 20_000,
      refetchInterval: DASHBOARD_ACTIVITY_REFETCH_MS,
      refetchOnWindowFocus: false,
      retry: shouldRetryDashboardRead,
    }
  )

  // The button asks the server to read again instead of answering from its 30-second copy.
  const refresh = useCallback(() => {
    setRefreshing(true)
    return queryClient
      .fetchQuery(getDashboardActivityQueryKey(companyId), ({signal}) =>
        getDashboardActivity(true, signal)
      )
      .catch(() => undefined)
      .finally(() => setRefreshing(false))
  }, [companyId, queryClient])

  return {...query, refresh, refreshing: refreshing || query.isFetching}
}

export const useDashboardLive = (companyId: number) =>
  useQuery<DashboardLiveSnapshot>(
    getDashboardLiveQueryKey(companyId),
    ({signal}) => getDashboardLive(signal),
    {
      enabled: companyId > 0,
      keepPreviousData: true,
      staleTime: 5_000,
      refetchInterval: (snapshot) =>
        isLiveSnapshotBusy(snapshot)
          ? DASHBOARD_LIVE_ACTIVE_REFETCH_MS
          : DASHBOARD_LIVE_IDLE_REFETCH_MS,
      refetchOnWindowFocus: true,
      retry: shouldRetryDashboardRead,
    }
  )
